/**
 * serverHealth — قطع‌کننده‌ی مدار (circuit breaker) برای وب‌سرویس.
 *
 * مشکل: وقتی وب‌سرویس در دسترس نیست، هر صفحه/کامپوننت درخواست خودش را می‌زند
 * (AppSetting، Session، Captcha، LogEvent، polling ...) و همه بی‌فایده شکست می‌خورند؛
 * ثبت همان خطاها هم خودش درخواست جدید (LogEvent) می‌سازد.
 *
 * راه‌حل:
 *   - اولین نشانه‌ی قطعی (خطای شبکه با navigator.onLine=true، یا ۵۰۲/۵۰۳/۵۰۴ از
 *     پراکسی/IIS) مدار را «باز» می‌کند → serverState.down = true.
 *   - تا مدار باز است، interceptor در baseApi.js درخواست‌های axios را بدون
 *     ارسال به شبکه رد می‌کند (خطای ServerDownError).
 *   - فقط یک probe سبک با backoff نمایی (۵s → ۱۰ → ۲۰ → ... → حداکثر ۶۰s) سرور را
 *     می‌سنجد؛ هر پاسخ HTTP غیر از ۵۰۲/۵۰۳/۵۰۴ یعنی سرور برگشته.
 *   - با برگشتن سرور، listenerهای onServerRecovered اجرا می‌شوند.
 *
 * قطعی اینترنتِ خودِ کاربر (navigator.onLine=false) جداست و در utils/network.js
 * مدیریت می‌شود؛ اینجا شمرده نمی‌شود.
 */

import { reactive } from "vue";
import { apiBasePath } from "@/config";

const MIN_DELAY = 5000;
const MAX_DELAY = 60000;
const DOWN_STATUSES = [502, 503, 504];
// پراکسی vue-cli (فقط dev) قطعی بک‌اند را با ۵۰۰ و این متن برمی‌گرداند و onError سفارشی را هم
// override می‌کند (node_modules/@vue/cli-service/lib/util/prepareProxy.js). ۵۰۰ِ واقعی بک‌اند این متن را ندارد.
const DEV_PROXY_ERROR = /^Proxy error: Could not proxy request/;

function isDownResponse(status, body) {
    return (
        DOWN_STATUSES.includes(status) ||
        (status === 500 && typeof body === "string" && DEV_PROXY_ERROR.test(body))
    );
}
// مسیری که سرویس‌ورکر کش نمی‌کند (در runtimeCaching نیست) تا پاسخ کش‌شده، «سالم» جا نزند.
const PROBE_PATH = "account/Session";

export const serverState = reactive({
    down: false,
    since: null,
    // زمان تلاش بعدی (ms epoch) برای نمایش شمارش معکوس
    nextRetryAt: null,
    checking: false,
});

let delay = MIN_DELAY;
let timer = null;
const recoveredListeners = new Set();

export class ServerDownError extends Error {
    constructor() {
        super("وب‌سرویس در دسترس نیست");
        this.name = "ServerDownError";
        this.isServerDown = true;
    }
}

function browserOffline() {
    return typeof navigator !== "undefined" && navigator.onLine === false;
}

/** آیا این خطای axios نشانه‌ی در دسترس نبودن سرور است؟ */
export function isServerDownFailure(error) {
    if (!error || error.isServerDown) return false;
    if (error.response) return isDownResponse(error.response.status, error.response.data);
    // بدون پاسخ: لغو عمدی یا آفلاین بودن خود کاربر را قطعی سرور حساب نکن
    if (error.code === "ERR_CANCELED" || error.__CANCEL__) return false;
    return !browserOffline();
}

function schedule() {
    clearTimeout(timer);
    serverState.nextRetryAt = Date.now() + delay;
    timer = setTimeout(probe, delay);
}

export function markServerDown() {
    if (serverState.down) return;
    serverState.down = true;
    serverState.since = Date.now();
    delay = MIN_DELAY;
    schedule();
}

function markServerUp() {
    clearTimeout(timer);
    const wasDown = serverState.down;
    serverState.down = false;
    serverState.since = null;
    serverState.nextRetryAt = null;
    delay = MIN_DELAY;
    if (wasDown) {
        recoveredListeners.forEach((fn) => {
            try {
                fn();
            } catch (e) {
                /* noop */
            }
        });
    }
}

/** یک بار سرور را می‌سنجد (بدون عبور از interceptor). true یعنی سالم. */
export function probe() {
    if (serverState.checking) return Promise.resolve(!serverState.down);
    clearTimeout(timer);
    if (browserOffline()) {
        // اینترنت خود کاربر قطع است؛ بعداً دوباره
        schedule();
        return Promise.resolve(false);
    }
    serverState.checking = true;
    return fetch(apiBasePath + PROBE_PATH + "?t=" + Date.now(), {
        method: "GET",
        credentials: "include",
        cache: "no-store",
    })
        .then((r) =>
            r.status === 500
                ? r.text().then((body) => !isDownResponse(r.status, body))
                : !isDownResponse(r.status)
        )
        .catch(() => false)
        .then((ok) => {
            serverState.checking = false;
            if (ok) {
                markServerUp();
            } else {
                delay = Math.min(delay * 2, MAX_DELAY);
                schedule();
            }
            return ok;
        });
}

/** تلاش دستی (دکمه‌ی «تلاش دوباره») — backoff را ریست می‌کند. */
export function retryNow() {
    delay = MIN_DELAY;
    return probe();
}

/** listener برای لحظه‌ی برگشت سرور؛ تابع لغو برمی‌گرداند. */
export function onServerRecovered(fn) {
    recoveredListeners.add(fn);
    return () => recoveredListeners.delete(fn);
}

export default {
    serverState,
    ServerDownError,
    isServerDownFailure,
    markServerDown,
    probe,
    retryNow,
    onServerRecovered,
};
